import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { useSocketMessage } from '@/hooks/socket/useSocket';
import { useNotificationPrefs } from './useNotificationPrefs';
import type { Agent, WsServerMessage } from '@/lib/types';

type TriggerStatus = 'needs_input' | 'error' | 'completed';

const TRIGGER_STATUSES: ReadonlySet<TriggerStatus> = new Set<TriggerStatus>([
  'needs_input',
  'error',
  'completed',
]);

function notificationForTrigger(
  agent: Agent,
  status: TriggerStatus,
): { title: string; body: string } {
  const label = agent.name || 'Agent';
  switch (status) {
    case 'needs_input':
      return { title: `${label} needs input`, body: 'Your agent is waiting on a reply.' };
    case 'error':
      return { title: `${label} errored`, body: 'The agent stopped with an error.' };
    case 'completed':
      return { title: `${label} completed`, body: 'The agent finished its task.' };
  }
}

/**
 * Watch `agent:updated` WebSocket messages and fire a native `Notification`
 * when an agent transitions into a user-configured trigger status.
 *
 * Suppression rules:
 * - the agent must belong to the signed-in user (`agent.userId` match) — the
 *   `agent:updated` broadcast is org-wide, and teammates' agents must not
 *   ping you
 * - permission must be 'granted'
 * - master toggle (`prefs.enabled`) and the matching per-status toggle must be on
 * - the app tab must NOT be actively viewing that agent's detail page while
 *   visible (no point notifying the user about the thing they're looking at)
 *
 * Mount once in the app shell.
 */
export function useAgentNotifications(): void {
  const { prefs } = useNotificationPrefs();
  const { user } = useAuth();
  const location = useLocation();

  const userIdRef = useRef(user?.id ?? null);
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user?.id]);

  const prefsRef = useRef(prefs);
  useEffect(() => {
    prefsRef.current = prefs;
  }, [prefs]);

  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const lastStatusRef = useRef<Map<string, Agent['status']>>(new Map());

  useSocketMessage((msg: WsServerMessage) => {
    if (msg.type !== 'agent:updated') return;
    const agent = msg.agent;
    const prev = lastStatusRef.current.get(agent.id);
    lastStatusRef.current.set(agent.id, agent.status);
    if (prev === agent.status) return;
    if (!TRIGGER_STATUSES.has(agent.status as TriggerStatus)) return;

    // Own-agents only. The broadcast is org-scoped, so without this every
    // teammate's agent would notify everyone. Agents without an owner
    // (legacy rows, service launches) are nobody's — skip them too.
    if (agent.userId === null || agent.userId === undefined) return;
    if (agent.userId !== userIdRef.current) return;

    const current = prefsRef.current;
    if (!current.enabled) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;

    const status = agent.status as TriggerStatus;
    if (status === 'needs_input' && !current.onNeedsInput) return;
    if (status === 'error' && !current.onError) return;
    if (status === 'completed' && !current.onCompleted) return;

    const viewingThisAgent =
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible' &&
      pathnameRef.current.includes(agent.id);
    if (viewingThisAgent) return;

    try {
      const { title, body } = notificationForTrigger(agent, status);
      new Notification(title, {
        body,
        icon: '/icon-192.png',
        tag: agent.id,
      });
    } catch {
      // Swallow — broken environments should not crash the shell.
    }
  });
}
